import { Button, Col, Input, Space, Typography } from "antd";
import { DispatcherMap } from "../../map/Map";
import getDestinationsByUserRequestHelper from "../../../utils/helpers/getDestinationsByUserRequestHelper";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../app/store";
import { useEffect, useState } from "react";
import { userRequestByIdAsync } from "../../../features/userRequest/userRequestByIdSlice";
import { UserRequestEditDTO } from "../../../shared/dtos/userRequestEditDTO";
import { LoadingOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { StatusTranslations } from "../../../shared/constants/Status";
import dayjs from "dayjs";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import {
  updateQRFeeUserRequest,
  updateUserRequestPayment,
} from "../../../services/userRequestApi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ContainerLayout from "../../common/containerLayout";

const { Text } = Typography;

const UserRequestEdit: React.FunctionComponent = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [userRequest, setUserRequest] = useState<UserRequestEditDTO | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [totalInput, setTotalInput] = useState(0);
  const [isTotalInput, setIsTotalInput] = useState(false);
  const [qrFeeInput, setQRFeeInput] = useState(0);
  const [isQRFeeInput, setIsQRFeeInput] = useState(false);
  const dispatch = useAppDispatch();
  const userRequestById = useAppSelector(
    (state: RootState) => state.userRequestById.value,
  );
  const userRequestByIdStatus = useAppSelector(
    (state: RootState) => state.userRequestById.status,
  );

  useEffect(() => {
    const getUserRequest = async () => {
      if (state && state?.id > 0 && userRequest === null) {
        setIsLoading(true);
        const userRequestId = state.id;
        state.id = 0;
        await dispatch(userRequestByIdAsync({ userRequestId }));
      }
    };
    const assignUserRequest = () => {
      if (userRequest === null) {
        setUserRequest(userRequestById as UserRequestEditDTO);
      } else if (userRequest?.id !== userRequestById?.id) {
        setUserRequest(userRequestById as UserRequestEditDTO);
      }
      if (userRequest?.id === userRequestById?.id) {
        setIsLoading(false);
      }
    };
    getUserRequest();
    assignUserRequest();
  }, [dispatch, userRequestByIdStatus, state, userRequestById, userRequest]);

  const handleTotalInput = (total: number) => {
    setIsTotalInput(true);
    setTotalInput(total ?? 0);
  };

  const handleQRFeeInput = (qrFee: number) => {
    if (!qrFee) return null;

    setIsQRFeeInput(true);
    setQRFeeInput(qrFee);
  };

  const handleTotalInputEdit = async (total: number) => {
    if (!total) return null;
    if (!userRequest) return null;

    await updateUserRequestPayment({
      userRequestId: userRequest.id,
      total,
    });
    setIsTotalInput(false);
    setTotalInput(total);
    await dispatch(userRequestByIdAsync({ userRequestId: userRequest.id }));
  };

  const handleQRFeeInputEdit = async (qrFee: number) => {
    if (!qrFee) return null;
    if (!userRequest) return null;

    await updateQRFeeUserRequest({
      userRequestId: userRequest.id,
      qrFee,
    });
    setIsQRFeeInput(false);
    setQRFeeInput(qrFee);
    await dispatch(userRequestByIdAsync({ userRequestId: userRequest.id }));
  };

  return (
    <ContainerLayout title="Detalles del viaje" showNotification={false}>
      {!isLoading && userRequest && (
        <>
          <Col span={8}>
            <Button
              style={{ padding: "1rem 0", marginBottom: ".5rem" }}
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(WebRoutes.TripTableRoute)}
              type="link"
            >
              Volver
            </Button>
            <Space direction="vertical">
              <Text>
                <b>Usuario: </b> {userRequest.customerName}
              </Text>
              <Text>
                <b>Conductor: </b> {userRequest.providerName}
              </Text>
              <Text>
                <b>Distancia: </b> {userRequest.distance}
              </Text>
              <Text>
                <b>Hora de inicio: </b>
                {dayjs(userRequest.startedAt).format("YYYY-MM-DD HH:mm:ss")}
              </Text>
              <Text>
                <b>Hora final: </b>
                {dayjs(userRequest.finishedAt).format("YYYY-MM-DD HH:mm:ss")}
              </Text>
              <Text>
                <b>Origen: </b> {userRequest.originAddress}
              </Text>
              {userRequest.destinations && userRequest.destinations.length > 1
                ? userRequest.destinations
                    .slice(0, -1)
                    .map((dest, index) => (
                      <Text key={dest.id || index}>
                        <b>Parada {index + 1}: </b> {dest.address}
                      </Text>
                    ))
                : null}
              <Text>
                <b>Destino: </b> {userRequest.destinationAddress}
              </Text>
              {!isTotalInput && (
                <Space>
                  <b>Total: </b>
                  {totalInput > 0 ? totalInput : userRequest.total}
                  <Button
                    onClick={() =>
                      handleTotalInput(
                        totalInput > 0
                          ? totalInput
                          : (userRequest.total as number),
                      )
                    }
                  >
                    Editar
                  </Button>
                </Space>
              )}
              {isTotalInput && (
                <Space>
                  <Input
                    value={totalInput}
                    type="number"
                    onChange={(e) => setTotalInput(Number(e.target.value))}
                  />
                  <Button
                    onClick={() => handleTotalInputEdit(totalInput as number)}
                  >
                    Guardar
                  </Button>
                  <Button onClick={() => setIsTotalInput(false)}>
                    Cancelar
                  </Button>
                </Space>
              )}
              {!isQRFeeInput && (
                <Space>
                  <b>Porcentaje de Comision QR: </b>
                  {qrFeeInput > 0 ? qrFeeInput : userRequest.qrFee}
                  <Button
                    onClick={() =>
                      handleQRFeeInput(
                        qrFeeInput > 0
                          ? qrFeeInput
                          : (userRequest.qrFee as number),
                      )
                    }
                  >
                    Editar
                  </Button>
                </Space>
              )}
              {isQRFeeInput && (
                <Space>
                  <Input
                    value={qrFeeInput}
                    type="number"
                    onChange={(e) => setQRFeeInput(Number(e.target.value))}
                  />
                  <Button
                    onClick={() => handleQRFeeInputEdit(qrFeeInput as number)}
                  >
                    Guardar
                  </Button>
                  <Button onClick={() => setIsQRFeeInput(false)}>
                    Cancelar
                  </Button>
                </Space>
              )}
              <Text>
                <b>Status: </b>
                {
                  StatusTranslations.find((x) => x.name === userRequest.status)
                    ?.translation
                }
              </Text>
            </Space>
          </Col>
          <Col span={16}>
            <DispatcherMap
              destinations={getDestinationsByUserRequestHelper(userRequest)}
            />
          </Col>
        </>
      )}
      {isLoading && <LoadingOutlined />}
    </ContainerLayout>
  );
};

export default UserRequestEdit;
